import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {motion} from 'framer-motion';

const {FiCheck, FiX, FiDollarSign, FiZap, FiArrowRight, FiTrendingDown, FiStar} = FiIcons;

const PricingTable = () => {
  const handleCTAClick = () => {
    window.open('https://register.yiksy.com/', '_blank');
  };

  const tableData = [
    {feature: "Content Creation", traditionalStack: "ChatGPT, Claude", cost: 20, yiksy: true},
    {feature: "Scheduling & Analytics", traditionalStack: "Buffer (7 channels)", cost: 35, yiksy: true},
    {feature: "Graphic Design", traditionalStack: "Canva", cost: 15, yiksy: true},
    {feature: "Social Media Manager", traditionalStack: "", cost: 250, yiksy: "Save countless hours"}
  ];

  const totalTraditionalCost = tableData.reduce((sum, item) => sum + item.cost, 0);
  const yiksyPrice = 39;

  return (
    <section id="pricing" className="bg-gradient-to-br from-gray-50 via-white to-blue-50 py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      {/* Enhanced background elements */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-40 -translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-l from-purple-100 to-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-x-1/3 translate-y-1/3"></div>
      <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-br from-blue-50 to-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>

      {/* Floating elements */}
      <div className="absolute top-20 left-20 w-16 h-16 bg-blue-100 rounded-full opacity-60 animate-float"></div>
      <div className="absolute bottom-32 right-32 w-12 h-12 bg-purple-100 rounded-full opacity-50 animate-bounce-slow"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{opacity: 0, y: 20}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-blue-50 to-blue-100 text-primary px-6 py-3 rounded-full text-sm font-semibold mb-8 backdrop-blur-sm border border-blue-200">
            <SafeIcon icon={FiStar} className="mr-2" />
            Unmatched Value
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Why Pay <span className="text-primary">More</span> for <span className="text-primary">Less</span>?
          </h2>
          <p className="text-lg sm:text-xl text-text-light max-w-3xl mx-auto leading-relaxed">
            See how Yiksy compares to using multiple separate tools
          </p>
        </motion.div>

        <motion.div 
          className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden relative"
          initial={{opacity: 0, y: 40}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
        >
          {/* Background pattern overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white to-purple-50/30"></div>

          {/* Table Header */}
          <div className="relative z-10 bg-gradient-to-r from-gray-50 via-blue-50 to-gray-50 px-6 sm:px-8 py-6 sm:py-8 border-b-2 border-gray-200">
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="col-span-1">
                <h3 className="text-base sm:text-lg font-bold text-text flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <SafeIcon icon={FiZap} className="text-blue-600 text-sm" />
                  </div>
                  Feature / Task
                </h3>
              </div>
              <div className="col-span-1">
                <h3 className="text-base sm:text-lg font-bold text-text">Traditional Stack</h3>
              </div>
              <div className="col-span-1 text-center">
                <h3 className="text-base sm:text-lg font-bold text-red-600 flex items-center justify-center">
                  <SafeIcon icon={FiDollarSign} className="mr-2" />
                  Monthly Cost
                </h3>
              </div>
              <div className="col-span-1 text-center">
                <h3 className="text-base sm:text-lg font-bold text-primary flex items-center justify-center">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-2">
                    <SafeIcon icon={FiZap} className="text-primary text-sm" />
                  </div>
                  Yiksy
                </h3>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="relative z-10 divide-y divide-gray-100">
            {tableData.map((row, index) => (
              <motion.div
                key={index}
                className="px-6 sm:px-8 py-6 sm:py-8 hover:bg-gray-50/50 transition-all duration-300 group"
                initial={{opacity: 0, x: -20}}
                whileInView={{opacity: 1, x: 0}}
                viewport={{once: true}}
                transition={{duration: 0.5, delay: index * 0.1}}
              >
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div className="col-span-1">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-100 transition-colors">
                        <span className="text-lg font-bold text-blue-600">{index + 1}</span>
                      </div>
                      <span className="text-base sm:text-lg font-semibold text-text">{row.feature}</span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm sm:text-base text-text-light bg-gray-100 px-3 py-2 rounded-lg inline-block">
                      {row.traditionalStack}
                    </span>
                  </div>
                  <div className="col-span-1 text-center">
                    <div className="inline-flex items-center bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
                      <span className="text-lg sm:text-xl font-bold text-red-600">${row.cost}</span>
                      <span className="text-sm text-red-500 ml-1">/mo</span>
                    </div>
                  </div>
                  <div className="col-span-1 text-center">
                    {typeof row.yiksy === 'boolean' ? (
                      <div className="flex justify-center">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shadow-lg">
                          <SafeIcon icon={FiCheck} className="text-green-600 text-xl" />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 px-4 py-3 rounded-xl">
                        <span className="text-sm sm:text-base font-semibold text-green-700">{row.yiksy}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Table Footer - Enhanced Totals */}
          <div className="relative z-10 bg-gradient-to-r from-red-50 via-white to-green-50 px-6 sm:px-8 py-8 sm:py-10 border-t-2 border-gray-200">
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="col-span-1">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
                    <SafeIcon icon={FiTrendingDown} className="text-gray-600 text-xl" />
                  </div>
                  <span className="text-lg sm:text-xl font-bold text-text">Total Monthly Cost</span>
                </div>
              </div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-center">
                <div className="bg-red-100 border-2 border-red-300 px-6 py-4 rounded-2xl shadow-lg">
                  <div className="text-2xl sm:text-3xl font-bold text-red-600">${totalTraditionalCost}</div>
                  <div className="text-sm text-red-500">per month</div>
                </div>
              </div>
              <div className="col-span-1 text-center">
                <div className="bg-green-100 border-2 border-green-300 px-6 py-4 rounded-2xl shadow-lg relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      WINNER
                    </div>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-lg sm:text-xl font-bold text-gray-400 line-through">$59</span>
                    <span className="text-2xl sm:text-3xl font-bold text-green-600">${yiksyPrice}</span>
                  </div>
                  <div className="text-sm text-green-500">per month</div>
                </div>
              </div>
            </div>

            {/* Savings highlight */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-2xl shadow-xl">
                <SafeIcon icon={FiTrendingDown} className="mr-3 text-xl" />
                <div>
                  <div className="text-lg font-bold">Save ${totalTraditionalCost - yiksyPrice} per month</div>
                  <div className="text-sm opacity-90">That's ${(totalTraditionalCost - yiksyPrice) * 12} saved annually!</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced CTA */}
        <motion.div 
          className="mt-12 sm:mt-16 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-3xl p-8 sm:p-12 text-white text-center relative overflow-hidden"
          initial={{opacity: 0, scale: 0.95}}
          whileInView={{opacity: 1, scale: 1}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
        >
          {/* Background elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-x-8 -translate-y-8 filter blur-xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-x-8 translate-y-8 filter blur-xl"></div>

          <div className="relative z-10">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-semibold mb-6">
              <SafeIcon icon={FiZap} className="mr-2" />
              Limited Time Offer
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">Unlock your all-in-one content system</h3>
            <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join hundreds who've already made the smart switch to our all-in-one platform
            </p>
            <motion.button
              className="bg-white text-primary px-10 sm:px-12 py-4 sm:py-5 rounded-xl text-lg font-bold hover:bg-blue-50 transition-all shadow-xl inline-flex items-center touch-manipulation group"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleCTAClick}
            >
              Build My Free Profile
              <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <p className="text-blue-100 text-sm mt-4">
              15-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingTable;